import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Table from '../../components/Table/Table';
import AddButton from '../../components/Button/AddButton';
import { useTranslation } from 'react-i18next';
import { AUDIOBOOK_TABLE } from '../../utils/constants';
import { Community, LinkRow, Row } from '../../types';
import AlertModal from '../../components/AlertModal';
import AudiobookImportDialog from './AudiobookImportDialog';
import { FormControlLabel, Checkbox } from '@mui/material';

const AudiobookScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    audiobookStore: { audiobooks, getAllAudiobooks, deleteAudiobook },
    userStore: { communities, user, getCommunities },
  } = useMst();
  const [selectedRow, setSelectedRow] = useState<Row | LinkRow>();
  const [result, setResult] = useState<Row[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [importDialogOpen, setImportDialogOpen] = useState<boolean>(false);
  const [showTrackedOnly, setShowTrackedOnly] = useState(false);

  const filterAudiobooks = useCallback(
    (communities: Community[], ownCommunities: Community[]) => {
      const filtered = communities.filter((community: Community) => {
        return ownCommunities.some((el) => {
          return el.id === community.id;
        });
      });
      const filteredAudiobooks = audiobooks.filter((audiobook) => {
        return filtered.find(({ community }) => community === audiobook.publisher);
      });
      return filteredAudiobooks;
    },
    [audiobooks]
  );

  useEffect(() => {
    const getAudiobooks = async () => {
      await getAllAudiobooks();

      if (user?.admin) {
        const filteredAudiobooks = showTrackedOnly ? audiobooks.filter((book) => book.isTimeTracked) : audiobooks;
        const tempResult: Row[] =
          filteredAudiobooks.length > 0
            ? filteredAudiobooks.map((audiobook) => ({
                id: audiobook.id,
                name: audiobook.name,
                value: `${audiobook.name} - ${audiobook.author}`,
                secondValue: audiobook.published ? t('published') : t('draft'),
                thirdValue: audiobook.isTimeTracked ? t('reports.tracked') : '',
              }))
            : [];
        setResult(tempResult);
      } else {
        if (communities && user?.communities.length) {
          const filteredByCommunity = filterAudiobooks(communities, user.communities);
          const filteredAudiobooks = showTrackedOnly
            ? filteredByCommunity.filter((book) => book.isTimeTracked)
            : filteredByCommunity;
          const tempResult: Row[] =
            filteredAudiobooks && filteredAudiobooks.length > 0
              ? filteredAudiobooks.map((audiobook) => ({
                  id: audiobook.id,
                  name: audiobook.name,
                  value: `${audiobook.name} - ${audiobook.author}`,
                  secondValue: audiobook.published ? t('published') : t('draft'),
                  thirdValue: audiobook.isTimeTracked ? t('reports.tracked') : '',
                }))
              : [];
          setResult(tempResult);
        }
      }
    };

    getAudiobooks();
  }, [getAllAudiobooks, getCommunities, user, communities, audiobooks, t, filterAudiobooks, showTrackedOnly]);

  useEffect(() => {
    if (!selectedRow?.id || modalOpen) return;
    navigate(`/audiobooks/${selectedRow.id}`);
  }, [selectedRow?.id, modalOpen, navigate]);

  const navigateTo = () => {
    navigate('/audiobooks/create');
  };

  const deleteBook = async () => {
    if (selectedRow?.id) {
      await deleteAudiobook(selectedRow.id.toString());
      await getAllAudiobooks();
      const filteredAudiobooks = showTrackedOnly ? audiobooks.filter((book) => book.isTimeTracked) : audiobooks;
      const tempResult: Row[] =
        filteredAudiobooks.length > 0
          ? filteredAudiobooks.map((audiobook) => ({
              id: audiobook.id,
              name: audiobook.name,
              value: `${audiobook.name} - ${audiobook.author}`,
              secondValue: audiobook.published ? t('published') : t('draft'),
              thirdValue: audiobook.isTimeTracked ? t('reports.tracked') : '',
            }))
          : [];
      setResult(tempResult);
      setSelectedRow(undefined);
    }
    setModalOpen(false);
  };

  const toggleDelete = (id: number) => {
    setModalOpen(true);
    setSelectedRow({ id: id });
  };

  const cancel = () => {
    setSelectedRow(undefined);
    setModalOpen(false);
  };

  return (
    <Layout title={t('audiobooks')}>
      <div>
        <AddButton onClick={navigateTo} text={t('add_audiobook')} />
        <AddButton onClick={() => setImportDialogOpen(true)} text={t('import_audiobook')} />
      </div>
      <div>
        <FormControlLabel
          control={<Checkbox checked={showTrackedOnly} onChange={(e) => setShowTrackedOnly(e.target.checked)} />}
          label={t('reports.show_tracked_only')}
        />
      </div>
      <Table
        tableType={AUDIOBOOK_TABLE}
        data={result}
        setSelectedRow={(value) => setSelectedRow({ id: value?.id, value: value?.value })}
        selectedRow={selectedRow}
        onDelete={(id) => toggleDelete(id)}
      />
      <AlertModal
        open={modalOpen}
        title="delete_audiobook"
        onClose={() => setModalOpen(!modalOpen)}
        leftButtonPress={cancel}
        rightButtonPress={deleteBook}
      />
      <AudiobookImportDialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} />
    </Layout>
  );
});

export default AudiobookScreen;
